import { useEffect, useState } from 'react';
import { categoryLabel } from '@/features/common/categoryLabel';
import { nomenclatureItemsOf } from '@/features/common/useNomenclatureItems';
import { LIBRARY_DOMAINS } from '@/features/common/useThematicDomains';
import type { NomenclatureItem } from '@/features/common/types/NomenclatureItem';
import { useLanguage } from '@/i18n/LanguageContext';

const CATEGORIES = 'CONTENT_CATEGORII';

// Both nomenclatures, because the text an item stores is whatever it was filed under when it was
// written: CONTENT_CATEGORII for everything up to T232, the thematic domains the field offers since
// (useThematicDomains). A name is looked for in the two of them, so neither generation of items
// loses its English name. One request per code and per page load, shared with every other reader
// of the same nomenclature (useNomenclatureItems).
function vocabulary(): Promise<NomenclatureItem[]> {
  return Promise.all([nomenclatureItemsOf(CATEGORIES), nomenclatureItemsOf(LIBRARY_DOMAINS)]).then(
    ([categories, domains]) => [...categories, ...domains],
  );
}

/**
 * The vocabulary itself, for a caller that needs the list rather than a name, like the news form's
 * line saying what a visitor reading in English will see. Asked for only while `wanted`; null until
 * the list is there, so a caller can tell "not yet" from "none".
 */
export function useCategoryItems(wanted = true): NomenclatureItem[] | null {
  const [items, setItems] = useState<NomenclatureItem[] | null>(null);

  useEffect(() => {
    if (!wanted) return;
    let live = true;
    void vocabulary().then((list) => {
      if (live) setItems(list);
    });
    return () => {
      live = false;
    };
  }, [wanted]);

  return items;
}

/**
 * The category as the page's language names it — see categoryLabel.
 *
 * Asked for only in English, the one language that needs a name other than the stored one. Until the
 * list arrives, and if it never does, the stored text is shown: a chip in Romanian a moment longer
 * rather than a page waiting on it.
 */
export function useCategoryLabels(): (stored: string) => string {
  const { language } = useLanguage();
  const items = useCategoryItems(language === 'en');
  return (stored) => categoryLabel(stored, language, items ?? []);
}
